/**
 * map-helpers.js
 * Utility functions for map calculations and operations
 */

/**
 * Calculate bounds that encompass all shapes
 * @param {Array} shapes - Array of shape instances
 * @returns {google.maps.LatLngBounds|null}
 */
export function getBounds(shapes) {
  if (!shapes || shapes.length === 0) {
    return null
  }

  const bounds = shapes.reduce((lastBounds, shape) => {
    return lastBounds.union(shape.getBounds())
  }, shapes[0].getBounds())

  return bounds
}

/**
 * Generate default paths for new shapes based on map viewport
 * @param {google.maps.Map} map - Google Maps instance
 * @param {string} type - Shape type ('area' or 'distance')
 * @returns {Array<google.maps.LatLng>}
 */
export function getNewPaths(map, type = 'area') {
  const interpolate = google.maps.geometry.spherical.interpolate
  const bounds = map.getBounds()

  const NECorner = bounds.getNorthEast()
  const SWCorner = bounds.getSouthWest()

  const NWCorner = new google.maps.LatLng(NECorner.lat(), SWCorner.lng())
  const SECorner = new google.maps.LatLng(SWCorner.lat(), NECorner.lng())

  const NWXOffset = interpolate(NWCorner, NECorner, .4)
  const NWYOffset = interpolate(NWCorner, SWCorner, .4)

  const NEXOffset = interpolate(NECorner, NWCorner, .4)
  const NEYOffset = interpolate(NECorner, SECorner, .4)

  const SEXOffset = interpolate(SECorner, SWCorner, .4)
  const SEYOffset = interpolate(SECorner, NWCorner, .4)

  const SWXOffset = interpolate(SWCorner, SECorner, .4)
  const SWYOffset = interpolate(SWCorner, NWCorner, .4)

  switch (type) {
    case 'area':
      return [
        new google.maps.LatLng(NWYOffset.lat(), NWXOffset.lng()),
        new google.maps.LatLng(NEYOffset.lat(), NEXOffset.lng()),
        new google.maps.LatLng(SEYOffset.lat(), SEXOffset.lng()),
        new google.maps.LatLng(SWYOffset.lat(), SWXOffset.lng())
      ]

    case 'distance':
      return []
  }
}

/**
 * Wait for map to be initialized (async polling)
 * @param {Ref} mapRef - Vue ref containing map instance
 * @returns {Promise}
 */
export async function waitForMap(mapRef) {
  while (mapRef.value === null) {
    await new Promise(resolve => setTimeout(resolve, 100))
  }
}

/**
 * Wait for shapes to be initialized (async polling)
 * @param {Ref} shapesRef - Vue ref containing shapes array
 * @returns {Promise}
 */
export async function waitForShapes(shapesRef) {
  while (shapesRef.value.length === 0) {
    await new Promise(resolve => setTimeout(resolve, 100))
  }
}
